import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardCheck, Star, Send, AlertOctagon, Trophy,
  ChevronRight, Check, ArrowLeft, ShieldCheck,
} from "lucide-react";

/**
 * Phase 5L — HOD Review + Management Approval page.
 * Pulls /my-pending and lets the current user act on each cycle.
 */
export default function AppraisalReview() {
  const [pending, setPending] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [cycle, setCycle] = useState(null);

  const load = async () => {
    const { data } = await api.get("/appraisal/my-pending");
    setPending(data);
  };
  useEffect(() => { load(); }, []);

  const openCycle = async (id) => {
    setActiveId(id);
    const { data } = await api.get(`/appraisal/cycles/${id}`);
    setCycle(data);
  };

  if (!pending) return <div className="text-sm text-slate-500">Loading…</div>;

  if (activeId && cycle) {
    return <CycleEditor cycle={cycle} onBack={() => { setActiveId(null); setCycle(null); load(); }} onSaved={load} />;
  }

  return (
    <div className="space-y-4" data-testid="appraisal-review">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <ClipboardCheck className="h-3 w-3" /> Phase 5L · Reviews
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Appraisal Reviews</h1>
        <p className="text-xs text-slate-500 mt-0.5">Pending appraisals that need your action.</p>
      </div>

      <PendingList title="HOD Reviews Pending" tone="amber" items={pending.as_hod} onOpen={openCycle} testid="hod-pending" />
      <PendingList title="Management Approval Pending" tone="blue" items={pending.as_management} onOpen={openCycle} testid="mgt-pending" />

      {pending.as_hod.length === 0 && pending.as_management.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-6 text-center" data-testid="all-clear">
          <Check className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm text-emerald-900 font-semibold">All caught up — no pending reviews 🎉</p>
        </div>
      )}
    </div>
  );
}

function PendingList({ title, tone, items, onOpen, testid }) {
  if (!items?.length) return null;
  const colors = { amber: "border-amber-200 bg-amber-50/30", blue: "border-blue-200 bg-blue-50/30" };
  return (
    <div className={`border rounded ${colors[tone]}`}>
      <div className="px-3 py-2 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-700">{title} ({items.length})</div>
      <div className="divide-y divide-slate-100">
        {items.map(c => (
          <button key={c.id} onClick={() => onOpen(c.id)} className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/60" data-testid={`${testid}-${c.id}`}>
            <div className="text-left">
              <div className="text-sm font-medium">{c.employee_name} <span className="text-[10px] text-slate-500">· {c.employee_code}</span></div>
              <div className="text-[11px] text-slate-500">{c.department_name || "—"} · {c.cycle_name}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
        ))}
      </div>
    </div>
  );
}

function CycleEditor({ cycle, onBack, onSaved }) {
  const [hodAnswers, setHodAnswers] = useState(() => {
    const m = {};
    (cycle.answers || []).forEach(a => { m[a.question_id] = a; });
    return m;
  });
  const [overall, setOverall] = useState(cycle.hod_review?.overall_comment || "");
  const [recommended, setRecommended] = useState(cycle.hod_review?.recommended_hike_pct ?? "");
  const [mgtForm, setMgtForm] = useState({
    final_hike_pct: cycle.mgt_approval?.final_hike_pct ?? "",
    grade: cycle.mgt_approval?.grade || "",
    mgt_comment: cycle.mgt_approval?.mgt_comment || "",
  });
  const [saving, setSaving] = useState(false);

  const setHod = (qid, patch) => setHodAnswers({
    ...hodAnswers,
    [qid]: { ...(hodAnswers[qid] || { question_id: qid }), ...patch },
  });

  const submitHod = async (submit) => {
    setSaving(true);
    try {
      await api.put(`/appraisal/cycles/${cycle.id}/hod-review`, {
        answers: Object.values(hodAnswers).map(a => ({
          question_id: a.question_id,
          hod_rating: a.hod_rating ?? null,
          hod_comment: a.hod_comment || "",
        })),
        overall_comment: overall,
        recommended_hike_pct: recommended === "" ? null : parseFloat(recommended),
        submit,
      });
      toast.success(submit ? "Sent to Management ✅" : "Draft saved");
      onSaved();
      onBack();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const submitMgt = async (approved) => {
    setSaving(true);
    try {
      await api.put(`/appraisal/cycles/${cycle.id}/mgt-approval`, {
        approved,
        final_hike_pct: mgtForm.final_hike_pct === "" ? null : parseFloat(mgtForm.final_hike_pct),
        grade: mgtForm.grade,
        mgt_comment: mgtForm.mgt_comment,
      });
      toast.success(approved ? "Approved 🎉" : "Sent back to HOD for re-review");
      onSaved(); onBack();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const isHodStage = cycle.status === "PENDING_HOD_REVIEW";
  const isMgtStage = cycle.status === "PENDING_MGT_APPROVAL";
  const contrib = cycle.contribution;

  return (
    <div className="space-y-4" data-testid="cycle-editor">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="back-btn"><ArrowLeft className="h-3.5 w-3.5 mr-1" />Back</Button>
        <h1 className="text-xl font-bold">{cycle.employee_name} — {cycle.cycle_name}</h1>
      </div>

      {/* Contribution snapshot */}
      {contrib && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50 border border-slate-200 rounded p-3 text-xs" data-testid="contrib-grid">
          <StatPill label="Delegation" v={`${contrib.delegation.completed}/${contrib.delegation.total} (${contrib.delegation.completion_pct}%)`} />
          <StatPill label="Checklist" v={`${contrib.checklist.completed}/${contrib.checklist.total} (${contrib.checklist.completion_pct}%)`} />
          <StatPill label="Attendance" v={`${contrib.attendance.attendance_pct}%`} />
          <StatPill label="Late punches" v={contrib.attendance.late_count} />
          <StatPill label="Leaves" v={Object.values(contrib.leaves).reduce((a, b) => a + b, 0)} />
          <StatPill label="Mistakes" v={contrib.mistakes.total} sev={contrib.mistakes.total > 0 ? "amber" : "ok"} />
          <StatPill label="Appreciations" v={contrib.appreciations.total} sev="ok" />
          <StatPill label="Period" v={`${contrib.period.from} → ${contrib.period.to}`} />
        </div>
      )}

      {/* Self appraisal narratives */}
      <div className="bg-white border border-slate-200 rounded p-4">
        <h3 className="text-xs font-bold uppercase mb-2">Employee Self-Appraisal</h3>
        <Narrative label="Key Achievements" value={cycle.self_appraisal?.key_achievements} />
        <Narrative label="Challenges Faced" value={cycle.self_appraisal?.challenges_faced} />
        <Narrative label="Training Needs" value={cycle.self_appraisal?.training_needs} />
      </div>

      {/* Questions */}
      <div className="space-y-3">
        {Object.entries(groupBy(cycle.questions || [], "category")).map(([cat, qs]) => (
          <div key={cat} className="bg-white border border-slate-200 rounded p-3">
            <h3 className="text-[11px] uppercase tracking-wider font-bold text-slate-700 mb-2">{cat}</h3>
            {qs.map(q => {
              const emp = (cycle.answers || []).find(a => a.question_id === q.id) || {};
              const hod = hodAnswers[q.id] || {};
              return (
                <div key={q.id} className="grid grid-cols-12 gap-2 items-start py-2 border-t border-slate-100 first:border-t-0">
                  <div className="col-span-4 text-xs text-slate-800">
                    <div>{q.text}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Weight: {q.weight}</div>
                  </div>
                  <div className="col-span-3 text-[11px] bg-slate-50 rounded px-2 py-1">
                    <div className="text-[9px] uppercase text-slate-500">Employee</div>
                    <div className="flex items-center gap-1">{emp.employee_rating ? `★${emp.employee_rating}` : "—"}</div>
                    <div className="text-[10px] text-slate-600 italic line-clamp-2">{emp.employee_comment || "—"}</div>
                  </div>
                  <div className="col-span-2">
                    <Rating value={hod.hod_rating} onChange={(v) => setHod(q.id, { hod_rating: v })} disabled={!isHodStage} testid={`hod-rate-${q.id}`} />
                  </div>
                  <div className="col-span-3">
                    <input type="text" value={hod.hod_comment || ""}
                            onChange={(e) => setHod(q.id, { hod_comment: e.target.value })}
                            disabled={!isHodStage}
                            placeholder="HOD comment"
                            className="w-full text-xs border border-slate-300 rounded px-2 py-1 h-7 disabled:bg-slate-50"
                            data-testid={`hod-comment-${q.id}`} />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* HOD action area */}
      {isHodStage && (
        <div className="bg-amber-50 border border-amber-200 rounded p-4 space-y-3" data-testid="hod-action">
          <h3 className="text-xs font-bold uppercase">HOD Final Comments & Recommendation</h3>
          <Textarea value={overall} onChange={(e) => setOverall(e.target.value)} rows={3} placeholder="Overall comment…" data-testid="hod-overall" />
          <div className="flex items-center gap-3">
            <Label className="text-xs">Recommended Hike %</Label>
            <Input type="number" step="0.5" value={recommended} onChange={(e) => setRecommended(e.target.value)} className="w-32" data-testid="hod-hike" />
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-amber-200">
            <Button variant="outline" onClick={() => submitHod(false)} disabled={saving}>Save Draft</Button>
            <Button onClick={() => submitHod(true)} disabled={saving} className="brand-bg brand-bg-hover text-white" data-testid="submit-mgt-btn">
              <Send className="h-3.5 w-3.5 mr-1" />Submit to Management
            </Button>
          </div>
        </div>
      )}

      {/* Management action area */}
      {isMgtStage && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4 space-y-3" data-testid="mgt-action">
          <h3 className="text-xs font-bold uppercase flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />Management Approval</h3>
          <div className="bg-white border border-slate-200 rounded p-2 text-xs">
            <div><strong>HOD weighted score:</strong> {cycle.hod_review?.weighted_score ?? "—"} / 5</div>
            <div><strong>HOD recommended hike:</strong> {cycle.hod_review?.recommended_hike_pct ?? "—"}%</div>
            <div className="mt-1"><strong>HOD comment:</strong> {cycle.hod_review?.overall_comment || "—"}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Final Hike %</Label>
              <Input type="number" step="0.5" value={mgtForm.final_hike_pct} onChange={(e) => setMgtForm({ ...mgtForm, final_hike_pct: e.target.value })} data-testid="final-hike" />
            </div>
            <div>
              <Label className="text-xs">Grade</Label>
              <Input value={mgtForm.grade} onChange={(e) => setMgtForm({ ...mgtForm, grade: e.target.value })} placeholder="e.g. A, A+, B" data-testid="grade" />
            </div>
          </div>
          <Textarea value={mgtForm.mgt_comment} onChange={(e) => setMgtForm({ ...mgtForm, mgt_comment: e.target.value })} rows={2} placeholder="Management comment…" />
          <div className="flex gap-2 justify-end pt-2 border-t border-blue-200">
            <Button variant="outline" onClick={() => submitMgt(false)} disabled={saving}>Send back to HOD</Button>
            <Button onClick={() => submitMgt(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-btn">
              <Check className="h-3.5 w-3.5 mr-1" />Approve
            </Button>
          </div>
        </div>
      )}

      {cycle.status === "APPROVED" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900">
          ✅ Approved by Management. Grade: <b>{cycle.mgt_approval?.grade || "—"}</b>, Hike: <b>{cycle.mgt_approval?.final_hike_pct || "—"}%</b>
        </div>
      )}
    </div>
  );
}

function Narrative({ label, value }) {
  return (
    <div className="mb-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-xs text-slate-800 whitespace-pre-wrap">{value || <span className="text-slate-400 italic">—</span>}</div>
    </div>
  );
}

function StatPill({ label, v, sev }) {
  const tone = sev === "amber" ? "bg-amber-50 text-amber-800 border-amber-200"
             : sev === "ok" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
             : "bg-white border-slate-200";
  return (
    <div className={`border rounded px-2 py-1 ${tone}`}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-xs font-bold font-mono">{v}</div>
    </div>
  );
}

function Rating({ value, onChange, disabled, testid }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} disabled={disabled} onClick={() => onChange(n)} className="p-0.5 disabled:opacity-50">
          <Star className={`h-4 w-4 ${(value || 0) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

function groupBy(arr, k) {
  const m = {};
  arr.forEach(it => { m[it[k]] = m[it[k]] || []; m[it[k]].push(it); });
  return m;
}
